// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { GetAllInstrumentConfigurationsResponse } from '@digitalasset/utility-operator-api-ts-client';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import {
  RejectedTransfer,
  ExecutedTransfer,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import { RequestState, RequestType, RowNew } from '../components/Table/types';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { filterInstConfigsByInstrument } from './filterInstConfigsByInstrument';

export type TransferUnion =
  | TemplateContract<RejectedTransfer>
  | TemplateContract<ExecutedTransfer>
  | InterfaceContract<TransferInstruction>;

const genericTransferMapper = (
  contract: TransferUnion,
  requestType: RequestType,
  requestState: RequestState,
  isInterface?: boolean,
  instConfigs?: GetAllInstrumentConfigurationsResponse,
) => {
  const commonFields = {
    contractId: contract.contractId,
    templateId: contract.templateId,
    requestType,
    requestState,
    isInterface,
  };
  const flattened: RowNew = {
    ...commonFields,
    receiver: '',
    reason: '',
    operator: '',
    provider: '',
    instrumentId: '',
    registrar: '',
    holdingLabel: '',
    sender: '',
    createdAt: '',
    amount: '',
    reference: '',
  };
  if ('interfaceId' in contract) {
    const { transfer } = contract.view(TransferInstruction);
    const config = filterInstConfigsByInstrument(
      transfer.instrumentId.id,
      transfer.instrumentId.admin,
      instConfigs,
    );
    const transferPayload = {
      sender: transfer.sender,
      receiver: transfer.receiver,
      amount: transfer.amount,
      executeBefore: transfer.executeBefore,
      registrar: transfer.instrumentId.admin,
      instrumentId: transfer.instrumentId.id,
      reference: transfer.meta.values['splice.lfdecentralizedtrust.org/reason'],
      provider: config?.payload.provider,
      operator: config?.payload.operator,
      createdAt: transfer.requestedAt,
      inputHoldingCids: transfer.inputHoldingCids,
    };
    return {
      ...commonFields,
      ...transferPayload,
    } as RowNew;
  }
  if ('templateId' in contract) {
    if ('operator' in contract.payload.transfer) {
      flattened.operator = contract.payload.transfer.operator;
    }
    if ('provider' in contract.payload.transfer) {
      flattened.provider = contract.payload.transfer.provider;
    }
    if ('reason' in contract.payload) {
      flattened.reason = contract.payload.reason;
    }
    if ('sender' in contract.payload.transfer) {
      flattened.sender = contract.payload.transfer.sender;
    }
    if ('receiver' in contract.payload.transfer) {
      flattened.receiver = contract.payload.transfer.receiver;
    }
    if ('reference' in contract.payload.transfer) {
      flattened.reference = contract.payload.transfer.reference;
    }
    if ('instrumentIdentifier' in contract.payload.transfer) {
      flattened.instrumentId = contract.payload.transfer.instrumentIdentifier.id;
      flattened.registrar = contract.payload.transfer.instrumentIdentifier.source;
    }
    if ('amount' in contract.payload.transfer) {
      flattened.amount = contract.payload.transfer.amount;
    }
    if ('createdAt' in contract) {
      flattened.createdAt = contract.createdAt;
    }
  }
  return flattened;
};

export const flattenTransfers = (
  contracts: TransferUnion[],
  requestState: RequestState,
  isInterface?: boolean,
  instConfigs?: GetAllInstrumentConfigurationsResponse,
) => {
  return contracts.map((c) =>
    genericTransferMapper(c, RequestType.Transfer, requestState, isInterface, instConfigs),
  );
};
